import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';
import { IWorkTime } from '../interface/workTimes';
import { IFilterOption } from '../interface/transaction';

interface WorkTimeContextType {
  workTime: IWorkTime | null;
  initialWorkTime: IWorkTime | null;
  updateWorkTime: (workTime: IWorkTime | null) => void;
  clearInitialWorkTime: () => void;
  activityFilter: IFilterOption | null;
  updateActivityFilter: (filter: IFilterOption | null) => void;
}

const WorkTimeContext = createContext<WorkTimeContextType | undefined>(undefined);

export const WorkTimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [workTime, setWorkTime] = useState<IWorkTime | null>(null);
  const [initialWorkTime, setInitialWorkTime] = useState<IWorkTime | null>(null);
  const [activityFilter, setActivityFilter] = useState<IFilterOption | null>(null);

  const updateWorkTime = useCallback((data: IWorkTime | null) => {
    if (data) {
      setInitialWorkTime((currentInitialWorkTime) => currentInitialWorkTime ?? data);
    } else {
      // The picker page and the dropdown share the filter choice for as long as the form is open,
      // and the form is over once the work time is reset.
      setActivityFilter(null);
    }
    setWorkTime(data);
  }, []);
  const clearInitialWorkTime = useCallback(() => {
    setInitialWorkTime(null);
  }, []);
  const workTimeContextValue = useMemo(
    () => ({
      workTime,
      updateWorkTime,
      initialWorkTime,
      clearInitialWorkTime,
      activityFilter,
      updateActivityFilter: setActivityFilter,
    }),
    [workTime, updateWorkTime, initialWorkTime, clearInitialWorkTime, activityFilter],
  );
  return (
    <WorkTimeContext.Provider value={workTimeContextValue}>{children}</WorkTimeContext.Provider>
  );
};

export const useWorkTime = () => {
  const context = useContext(WorkTimeContext);
  if (!context) {
    throw new Error('useWorkTime must be used within a WorkTimeProvider');
  }
  return context;
};
